import { FC, useState } from 'react';
import { ChevronDown, EyeIcon, FolderKanban } from 'lucide-react';
import { cn } from '@new/lib';
import { ProjectSummary } from '@new/features/courses/utils/projectHelpers';

interface StudentCourseProjectProps {
  summary: ProjectSummary;
}

export const StudentCourseProject: FC<StudentCourseProjectProps> = ({ summary }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { projectName, status, submissions } = summary;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-(--neutral-white-NW800) bg-(--neutral-white-NW1000) p-4 shadow-sm">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full cursor-pointer items-center justify-between gap-3 border-0 bg-transparent p-0 text-left"
        aria-expanded={isOpen}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-(--primary-O200)">
            <FolderKanban className="size-4 text-(--primary-O800)" />
          </div>

          <div className="flex min-w-0 flex-col gap-2">
            <h6 className="[font-size:var(--fs-h6)] [font-family:var(--font-montserrat)]! m-0! truncate font-semibold! text-(--neutral-black-NB800)!">
              Project
            </h6>

            <div className="flex flex-wrap items-center gap-2">
              <p className="[font-size:var(--fs-l4)] m-0! font-medium text-(--neutral-black-NB700)">
                {projectName}
              </p>

              <div className="size-1 rounded-full bg-(--neutral-black-NB700)" />

              <p className="[font-size:var(--fs-l4)] m-0! font-medium text-(--neutral-black-NB700)">
                {status}
              </p>
            </div>
          </div>
        </div>

        <ChevronDown
          className={cn(
            'size-5 shrink-0 text-(--neutral-black-NB700) transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="flex flex-col gap-3 border-t border-(--neutral-white-NW800) pt-3">
          <p className="[font-size:var(--fs-p4)] m-0! font-semibold text-(--neutral-black-NB800)">
            Submission History
          </p>

          {submissions.length === 0 ? (
            <p className="[font-size:var(--fs-l4)] m-0! font-medium text-(--neutral-black-NB700)">
              Project submission details are not available yet.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {submissions.map((submission) => (
                <div
                  key={submission.id}
                  className="flex flex-col gap-3 rounded-lg border border-(--primary-O300) bg-(--primary-O200)/50 p-3 shadow-xs"
                >
                  <div className="flex justify-between gap-4">
                    <div className="flex min-w-0 flex-col gap-2">
                      <span
                        className={cn(
                          'm-0! w-fit rounded-full border px-2 py-0.5 text-xs font-semibold',
                          submission.status === 'Approved'
                            ? 'border-(--success-S300) bg-(--success-S200) text-(--success-S700)'
                            : 'border-(--danger-D300) bg-(--danger-D200) text-(--danger-D700)'
                        )}
                      >
                        {submission.status}
                      </span>

                      <p className="m-0! truncate font-semibold text-(--neutral-black-NB800)">
                        {submission.fileName}
                      </p>
                    </div>

                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <p className="m-0! font-semibold text-(--neutral-black-NB800)">
                        {submission.reviewDate}
                      </p>

                      {submission.fileUrl ? (
                        <a
                          href={submission.fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 text-(--primary-O900)! no-underline"
                        >
                          <EyeIcon className="size-4 text-(--primary-O900)" />

                          <span className="m-0! font-semibold text-(--primary-O900)">View</span>
                        </a>
                      ) : (
                        <div className="flex items-center gap-1 opacity-60">
                          <EyeIcon className="size-4 text-(--primary-O900)" />

                          <span className="m-0! font-semibold text-(--primary-O900)">View</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-start gap-2 rounded-xl border border-(--primary-O400) bg-(--neutral-white-NW1000) p-3">
                    <span className="[font-size:var(--fs-l5)] font-semibold uppercase text-(--neutral-black-NB700)">
                      Instructor Review
                    </span>

                    <span className="[font-size:var(--fs-p4)] m-0! font-medium text-(--neutral-black-NB800)">
                      {submission.instructorNote}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
